/**
 * @file api.ts
 * @brief TanStack Query hooks for the Super Admin → Billing & Pricing
 *        feature surface.
 *
 * @details
 * One module, six hooks:
 *   useSystemSettings    → GET  /api/admin/settings
 *   useUpdateSettings    → PUT  /api/admin/settings
 *   usePricingConfig     → GET  /api/admin/pricing-config
 *   useUpdatePricingConfig → PUT  /api/admin/pricing-config
 *   useCostStatus        → GET  /api/admin/cost-status
 *   useSendTestCostAlert → POST /api/admin/cost-alert/test
 *   useRecomputeCosts    → POST /api/admin/analytics/recompute-session-costs
 *   useBillingCountries / useUpdateBillingCountries
 *                        → GET / PUT /api/admin/billing-countries
 *   useAdminPlans        → GET  /api/admin/plans (Free, Pro, Business,
 *                          Enterprise cards; saved via useUpdateSettings)
 *   usePlanImpacts       → GET  /api/admin/plans/impact (B20: who each
 *                          pending change reaches, read-only counts)
 *
 * Each mutation invalidates only the queries it logically touches so
 * the panel doesn't refetch unrelated data:
 *   • Settings save → re-fetch settings + cost status (the alert
 *     threshold/email/enabled trio lives in settings but feeds
 *     cost-status).
 *   • Pricing-config save → re-fetch pricing config only.
 *   • Test alert → no cache touch (it never sets LAST_COST_ALERT_DATE).
 *   • Recompute → no cache touch; the page surfaces the `updated`
 *     count via a toast.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/{client,endpoints}
 *
 * @references
 * - BACKEND/src/features/admin/routes.js
 * - WEBPANEL/src/features/billing/schemas.ts
 *
 * @author DES
 * @date 26-05-2026
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { ApiClientError, apiGet, apiPost, apiPut } from "@/shared/api/client";
import { adminEndpoints } from "@/shared/api/endpoints";
import {
  AdminPlansResponseSchema,
  BillingCountriesResponseSchema,
  PlanImpactSchema,
  CostAlertTestResponseSchema,
  CostStatusSchema,
  PricingConfigResponseSchema,
  RecomputeResponseSchema,
  SystemSettingsResponseSchema,
  type AdminPlansResponse,
  type BillingCountriesResponse,
  type PlanImpact,
  type CostAlertTestResponse,
  type CostStatus,
  type PricingConfigResponse,
  type RecomputeRequest,
  type RecomputeResponse,
  type SystemSettingsResponse,
} from "./schemas";

// ----------------------------------------------------------------------
// Query key factory — namespaced so it's easy to invalidate the
// whole feature in one shot if we ever need to.
// ----------------------------------------------------------------------
export const billingKeys = {
  all: ["admin", "billing"] as const,
  settings: () => [...billingKeys.all, "settings"] as const,
  pricingConfig: () => [...billingKeys.all, "pricing-config"] as const,
  costStatus: () => [...billingKeys.all, "cost-status"] as const,
  billingCountries: () =>
    [...billingKeys.all, "billing-countries"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  planImpact: (key: string, value: string) =>
    [...billingKeys.all, "plan-impact", key, value] as const,
};

// ----------------------------------------------------------------------
// System Settings — read
// `staleTime: 0` because saves happen on the same page and we always
// want the freshest values reflected after a write.
// ----------------------------------------------------------------------
export function useSystemSettings(
  options?: Partial<UseQueryOptions<SystemSettingsResponse>>,
) {
  return useQuery<SystemSettingsResponse>({
    queryKey: billingKeys.settings(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.settings);
      return SystemSettingsResponseSchema.parse(raw);
    },
    ...options,
  });
}

// ----------------------------------------------------------------------
// System Settings — write
// The backend accepts `{ settings: { key: value, ... } }` where every
// `value` is coerced to String() server-side. We send the partial
// patch — only keys that actually changed — to keep the audit trail
// (`updated_at`) accurate for unrelated keys.
// ----------------------------------------------------------------------
export interface UpdateSettingsInput {
  settings: Record<string, string | number | boolean>;
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, UpdateSettingsInput>({
    mutationFn: async ({ settings }) =>
      apiPut<unknown>(adminEndpoints.settings, { settings }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.settings() });
      qc.invalidateQueries({ queryKey: billingKeys.costStatus() });
      qc.invalidateQueries({ queryKey: billingKeys.plans() });
    },
  });
}

// ----------------------------------------------------------------------
// Launch plans (BACKEND B3, 22-10-2026): Free, Pro, Business, Enterprise,
// each with its settings (plain label, kind, stored value, what billing
// reads now) and rates per currency. Saved with useUpdateSettings.
// ----------------------------------------------------------------------
export function useAdminPlans() {
  return useQuery<AdminPlansResponse>({
    queryKey: billingKeys.plans(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.plans);
      return AdminPlansResponseSchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// Plan change impact (BACKEND B20, 24-10-2026): for each pending change,
// when it applies and how many users / companies it reaches. Read-only.
// "missing" = the backend has no impact endpoint yet (404 until B20 is
// deployed): the bar shows "Affects: –" and does not block Save on it.
// ----------------------------------------------------------------------
export type PlanImpactState =
  | { status: "loading" }
  | { status: "ok"; data: PlanImpact }
  | { status: "missing" }
  | { status: "error"; message: string };

// DEV preview only (dev/PlanPricesPreview.tsx): stubbed counts, no request.
let impactStub: ((key: string, value: string) => PlanImpactState) | null = null;
export function __setPlanImpactStub(fn: typeof impactStub) {
  if (import.meta.env.DEV) impactStub = fn;
}

export function usePlanImpacts(
  changes: { key: string; value: string }[],
  enabled: boolean,
): Record<string, PlanImpactState> {
  const results = useQueries({
    queries: changes.map((c) => ({
      queryKey: billingKeys.planImpact(c.key, c.value),
      enabled,
      staleTime: 30_000,
      retry: false,
      queryFn: async () => {
        if (impactStub) return impactStub(c.key, c.value);
        try {
          const qs = new URLSearchParams({ key: c.key, value: c.value }).toString();
          const raw = await apiGet<unknown>(`${adminEndpoints.planImpact}?${qs}`);
          return { status: "ok", data: PlanImpactSchema.parse(raw) } as PlanImpactState;
        } catch (err) {
          if (err instanceof ApiClientError && err.status === 404) return { status: "missing" } as PlanImpactState;
          throw err;
        }
      },
    })),
  });
  const out: Record<string, PlanImpactState> = {};
  changes.forEach((c, i) => {
    const r = results[i];
    out[c.key] = r?.data
      ? r.data
      : r?.isError
        ? { status: "error", message: r.error instanceof Error ? r.error.message : "Couldn't count." }
        : { status: "loading" };
  });
  return out;
}

// ----------------------------------------------------------------------
// Pricing Config — read (OpenAI service rates)
// ----------------------------------------------------------------------
export function usePricingConfig(
  options?: Partial<UseQueryOptions<PricingConfigResponse>>,
) {
  return useQuery<PricingConfigResponse>({
    queryKey: billingKeys.pricingConfig(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.pricingConfig);
      return PricingConfigResponseSchema.parse(raw);
    },
    ...options,
  });
}

// ----------------------------------------------------------------------
// Pricing Config — write
// Backend expects `{ pricing: { service_name: cost_per_unit_cents, ... } }`
// with `value` parsed as float. We send only the rows the form
// touched so unrelated services don't get re-stamped.
// ----------------------------------------------------------------------
export interface UpdatePricingInput {
  pricing: Record<string, number>;
}

export function useUpdatePricingConfig() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, UpdatePricingInput>({
    mutationFn: async ({ pricing }) =>
      apiPut<unknown>(adminEndpoints.pricingConfig, { pricing }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.pricingConfig() });
    },
  });
}

// ----------------------------------------------------------------------
// Cost Status — read (live "today vs threshold")
// Auto-refresh every 60s so the pill stays current while the page
// stays open. (26-05-2026)
// ----------------------------------------------------------------------
export function useCostStatus(
  options?: Partial<UseQueryOptions<CostStatus>>,
) {
  return useQuery<CostStatus>({
    queryKey: billingKeys.costStatus(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.costStatus);
      return CostStatusSchema.parse(raw);
    },
    refetchInterval: 60_000,
    ...options,
  });
}

// ----------------------------------------------------------------------
// Test alert email (06-10-2026)
// Sends ONE real email to COST_ALERT_EMAIL so the recipient and the mail
// transport can be checked. It used to run the threshold check, which sends
// nothing below the threshold, so "Send test alert" usually sent nothing.
// Related: BACKEND features/billing/cost-alert.service.js sendTestCostAlert
// ----------------------------------------------------------------------
export function useSendTestCostAlert() {
  return useMutation<CostAlertTestResponse, Error, void>({
    mutationFn: async () => {
      const raw = await apiPost<unknown>(adminEndpoints.testCostAlert, {});
      return CostAlertTestResponseSchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// Recompute Session Costs (date-range tool)
// One-shot — no cache invalidation; the toast surfaces `updated` count.
// ----------------------------------------------------------------------
export function useRecomputeCosts() {
  return useMutation<RecomputeResponse, Error, RecomputeRequest>({
    mutationFn: async (input) => {
      const raw = await apiPost<unknown>(
        adminEndpoints.recomputeSessionCosts,
        input,
      );
      return RecomputeResponseSchema.parse(raw);
    },
  });
}

// ----------------------------------------------------------------------
// Billing countries — the plans block-list (BACKEND B1, 22-10-2026).
// PUT sends the WHOLE list (`[]` = block nothing) and returns the GET body.
// ----------------------------------------------------------------------
export function useBillingCountries() {
  return useQuery<BillingCountriesResponse>({
    queryKey: billingKeys.billingCountries(),
    queryFn: async () => {
      const raw = await apiGet<unknown>(adminEndpoints.billingCountries);
      return BillingCountriesResponseSchema.parse(raw);
    },
  });
}

export interface UpdateBillingCountriesInput {
  blocked: string[];
  reason?: string;
}

export function useUpdateBillingCountries() {
  const qc = useQueryClient();
  return useMutation<BillingCountriesResponse, Error, UpdateBillingCountriesInput>({
    mutationFn: async (body) => {
      const raw = await apiPut<unknown>(adminEndpoints.billingCountries, body);
      return BillingCountriesResponseSchema.parse(raw);
    },
    onSuccess: (data) => {
      qc.setQueryData(billingKeys.billingCountries(), data);
    },
  });
}
